import { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

import { uniqueIdTransfer } from '@/apiCalls/uniqueIDTransferHelper';
import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EEventType } from '@/components/eventType';
import { Header } from '@/components/Header';
import { PlusIconCircle } from '@/components/PlusIconCircle';
import { ProgressBar } from '@/components/ProgressBar';
import {
    selectCreditCards,
    selectHomeandContents,
    selectMotorVehicles,
    selectOther,
    selectPersonalLoans,
    selectPlantandEquipment,
    selectRealEstates,
    selectSavings,
    selectSuperandShares,
} from '@/context/borrower';
import { EModals, setModal } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { useFieldCopy } from '@/copy';
import { useScreenCopy } from '@/copy';
import {
    isAllCreditCardsComplete,
    isAllMotorVehicleAssetsComplete,
    isAllPersonalLoansComplete,
    isAllRealEstatesComplete,
} from '@/utils/checkAssetsAndLiabilities';
import { isMotorVehicleUndefined } from '@/utils/isMotorVehicleUndefined';
import { isRealEstateUndefined } from '@/utils/isRealEstateUndefined';

import { AssetsAndLiabilitiesButtons } from './AssetsAndLiabilitiesButtons';

export const testId = 'AssetsAndLiabilities';

export function AssetsAndLiabilities(): JSX.Element {
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState(false);

    // ***** CONSTANTS *****
    const baseId = 'screen-assetsAndLiabilities';

    // ***** Redux State *****
    const dispatch = useAppDispatch();

    const screenCopy = useScreenCopy('assetsAndLiabilities');

    const fields = useFieldCopy();

    const realEstates = useAppSelector(selectRealEstates);
    const motorVehicles = useAppSelector(selectMotorVehicles);
    const creditCards = useAppSelector(selectCreditCards);
    const personalLoans = useAppSelector(selectPersonalLoans);

    // Savings and more section
    const plantAndEqu = useAppSelector(selectPlantandEquipment);
    const homeAndContent = useAppSelector(selectHomeandContents);
    const savings = useAppSelector(selectSavings);
    const superAndShares = useAppSelector(selectSuperandShares);
    const others = useAppSelector(selectOther);
    const { cardListRows } = useCopy();

    const tickIconSavingsOrMore =
        plantAndEqu || homeAndContent || savings || superAndShares || others
            ? true
            : false;

    const savingsAndMoreArray = [
        plantAndEqu,
        homeAndContent,
        savings,
        superAndShares,
        others,
    ];

    const errorCss = 'error';

    const handleOnClickRealEstate = (): void => {
        uniqueIdTransfer({
            eventType: EEventType.TOGGLE,
            applicationData: {
                id: `${baseId}-toggle-expand-realEstate`,
            },
        });
        dispatch(setModal(EModals.REAL_ESTATES));
    };
    const handleOnClickMotorVehicles = (): void => {
        uniqueIdTransfer({
            eventType: EEventType.TOGGLE,
            applicationData: {
                id: `${baseId}-toggle-expand-motorVehicles`,
            },
        });
        dispatch(setModal(EModals.MOTOR_VEHICLES_ASSETS));
    };
    const handleOnClickSavings = (): void => {
        uniqueIdTransfer({
            eventType: EEventType.TOGGLE,
            applicationData: {
                id: `${baseId}-toggle-expand-savingsAndMore`,
            },
        });
        dispatch(setModal(EModals.SAVINGS_OR_MORE));
    };
    const handleOnClickCreditCard = (): void => {
        uniqueIdTransfer({
            eventType: EEventType.TOGGLE,
            applicationData: {
                id: `${baseId}-toggle-expand-creditCards`,
            },
        });
        dispatch(setModal(EModals.CREDIT_CARDS));
    };
    const handleOnClickPersonalLoans = (): void => {
        uniqueIdTransfer({
            eventType: EEventType.TOGGLE,
            applicationData: {
                id: `${baseId}-toggle-expand-personalLoans`,
            },
        });
        dispatch(setModal(EModals.PERSONAL_LOANS));
    };

    const assetsAndLiabilities = [
        {
            name: fields.assetCards.realEstate,
            item: realEstates,
            onClick: handleOnClickRealEstate,
            ref: 'realEstate',
            tickIcon: isAllRealEstatesComplete(),
            isUndefined: isRealEstateUndefined(),
        },
        {
            name: fields.assetCards.motorVehicle,
            item: motorVehicles,
            onClick: handleOnClickMotorVehicles,
            ref: 'motorVehicle',
            tickIcon: isAllMotorVehicleAssetsComplete(),
            isUndefined: isMotorVehicleUndefined(),
        },
        {
            name: fields.assetCards.savings,
            ref: 'savingsAndMore',
            item: savingsAndMoreArray,
            onClick: handleOnClickSavings,
            tickIcon: tickIconSavingsOrMore,
            isUndefined: true,
        },
        {
            name: fields.assetCards.creditCards,
            item: creditCards,
            onClick: handleOnClickCreditCard,
            ref: 'creditCards',
            tickIcon: isAllCreditCardsComplete(),
            isUndefined: creditCards?.length === 0,
        },
        {
            name: fields.assetCards.personalLoans,
            item: personalLoans,
            onClick: handleOnClickPersonalLoans,
            ref: 'personalLoans',
            tickIcon: isAllPersonalLoansComplete(),
            isUndefined: personalLoans?.length === 0,
        },
    ];

    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />
                <DisplayHeading
                    title={screenCopy.title}
                    infoText={screenCopy.subtitle}
                />
                {/* Todo: This shouldn't be here. It's reuseable with supporting docs for a start and secondly it's logic which should be in it's own component */}
                {assetsAndLiabilities.map((item, index) => (
                    <div
                        className={`_flex justify-between align-center cursor-pointer ${
                            cardListRows
                                ? 'bg-white border border-cardEdge rounded-2xl p-4 mb-2.5'
                                : 'm-2  p-4 border rounded-lg border-[#BDBDBD] 1px solid'
                        }
               ${!item.tickIcon && !item.isUndefined && errorCss}`}
                        onClick={item.onClick}
                        key={index}
                    >
                        <div className={cardListRows ? 'text-sm' : ''}>
                            {item.name}
                        </div>
                        <div className={`flex-center `}>
                            {item.tickIcon ? (
                                <CheckCircleIcon className='h-5 w-5 text-success' />
                            ) : (
                                <div id={`${baseId}-toggle-expand-${item.ref}`}>
                                    <PlusIconCircle />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                <AssetsAndLiabilitiesButtons
                    baseId={baseId}
                    setShowErrors={setShowErrors}
                />
            </DisplayComponent>
        </div>
    );
}
